import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { translateSignal } from '@jsverse/transloco';
import { ButtonConfig, ModalService } from '@ui-lib';
import { AlertsService } from '@common/services/alerts/alerts.service';
import { actionKeyToRoute } from '@common/services/alerts/action-route.util';
import { AuthService } from '@common/services/auth/auth.service';
import { AlertStateEnum } from '@common/enums/alert-state.enum';
import { AlertTypeEnum } from '@common/enums/alert-type.enum';
import { Flow } from '@common/interfaces/alerts/alerts.interface';
import { ROUTE_PATHS } from '@common/utils/route-paths.const';
import {
  BlockedPopupCopy,
  INVESTMENT_ACTION_POPUP,
  INVESTMENT_GENERIC_ACTION_POPUP,
  INVESTMENT_GENERIC_BLOCKED_POPUP,
  INVESTMENT_MINOR_PROXY_PENDING_REVIEW_POPUP,
  INVESTMENT_MINOR_PROXY_PENDING_SUBMISSION_POPUP,
  INVESTMENT_STATE_POPUP,
} from './investment-access.constants';

/**
 * Central gate for entering the Investment Flow. Reads the Alerts API's
 * `investment` flow (via AlertsService) and, when the user has a pending or
 * rejected blocking status, opens a config-driven popup (reusing ModalService)
 * instead of letting them proceed. Generic: any non-`passed` state blocks, and
 * the popup copy/navigation is resolved from the flow's action/state — no
 * per-status hardcoding.
 */
@Injectable({ providedIn: 'root' })
export class InvestmentAccessService {
  private readonly alerts = inject(AlertsService);
  private readonly modal = inject(ModalService);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  // titleKey/descriptionKey are dynamic (vary per flow state); updated in openBlockedPopup() before modal opens.
  private readonly titleKey = signal('');
  private readonly descriptionKey = signal('');
  private readonly titleText = translateSignal(this.titleKey);
  private readonly descriptionText = translateSignal(this.descriptionKey);

  /** True when the investment flow is in any non-passed (blocking) state. */
  isInvestmentBlocked(): boolean {
    const flow = this.alerts.investmentFlow();
    return !!flow && flow.state !== AlertStateEnum.PASSED;
  }

  /**
   * Resolves the block decision, fetching alerts first only when none are
   * cached (e.g. a cold deep-link to /investment/:fund, which authGuard skips).
   * Fails open on a fetch error — the in-flow server check is the safety net.
   */
  ensureFreshThenIsBlocked(): Observable<boolean> {
    if (this.alerts.alertsData()) {
      return of(this.isInvestmentBlocked());
    }
    return this.alerts.fetchAlerts().pipe(
      map(() => this.isInvestmentBlocked()),
      catchError(() => of(false))
    );
  }

  /** Opens the blocking-status popup for the current investment flow. */
  openBlockedPopup(): void {
    const flow = this.alerts.investmentFlow();
    if (!flow) return;

    const copy = this.resolveCopy(flow);
    this.titleKey.set(copy.titleKey);
    this.descriptionKey.set(copy.descriptionKey);

    this.modal.open({
      testId: 'investment_blocked_modal',
      icon: copy.icon,
      title: this.titleText,
      message: this.descriptionText,
      buttons: this.buildButtons(flow, copy),
      showCloseButton: true,
      // Design (Figma 1513-39341 / 1513-40041): 23.4375rem (375px) modal,
      // centered content, full-width buttons stacked vertically (primary on
      // top). Keep the modal's default adaptive bg-surface-bg-generic-white
      // (= #FAFAFA in light, dark-safe) rather than a hardcoded hex.
      modalClass: '!w-[23.4375rem] !max-w-[calc(100vw-2rem)]',
      // icon → title gap = measure-sm (12px)
      mediaClass: '!mb-measure-sm',
      titleClass:
        'text-typography-neutral-deep text-font-size-paragraph-lg leading-line-height-paragraph-lg font-semibold text-center wrap-break-word',
      messageClass:
        'text-typography-neutral-primary text-font-size-label-md leading-line-height-label-md font-medium text-center wrap-break-word',
      // title → description gap = measure-4xs (4px)
      contentClass: '!gap-measure-4xs',
      // stacked full-width, 12px gap (base gap-measure-sm), 24px above (measure-2xl)
      buttonsClass: '!flex-col !items-stretch !mt-measure-2xl',
    });
  }

  /**
   * Route segments for the flow's primary action. External `action.target`
   * URLs (rejected → configured action) are handled in navigatePrimary().
   */
  resolveInvestmentActionRoute(): string[] {
    const flow = this.alerts.investmentFlow();
    if (flow?.action?.key) {
      const hasNationalId = !!this.authService.authData()?.nationalId;
      return actionKeyToRoute(flow.action.key, hasNationalId);
    }
    return [ROUTE_PATHS.HOME];
  }

  // ── internals ──────────────────────────────────────────────────────────

  private resolveCopy(flow: Flow): BlockedPopupCopy {
    if (flow.reason?.gate === AlertTypeEnum.MINOR_PROXY) {
      return flow.reason.status === 'pending_review'
        ? INVESTMENT_MINOR_PROXY_PENDING_REVIEW_POPUP
        : INVESTMENT_MINOR_PROXY_PENDING_SUBMISSION_POPUP;
    }
    if (flow.state === AlertStateEnum.ACTION_REQUIRED && flow.action) {
      return (
        INVESTMENT_ACTION_POPUP[flow.action.key] ??
        INVESTMENT_GENERIC_ACTION_POPUP
      );
    }
    return (
      INVESTMENT_STATE_POPUP[flow.state] ?? INVESTMENT_GENERIC_BLOCKED_POPUP
    );
  }

  private buildButtons(flow: Flow, copy: BlockedPopupCopy): ButtonConfig[] {
    // No action → single "Back to home" (closes + navigates home).
    if (!copy.primaryKey) {
      return [
        {
          id: 'investment-blocked-back-home',
          label: 'investment_popup_back_home',
          variant: 'tertiary',
          size: 'md',
          btnClass: '!w-full',
          onClick: () => {
            this.modal.close();
            this.router.navigate([ROUTE_PATHS.HOME]);
          },
        },
      ];
    }

    // Has action → action CTA (primary, on top) + "Maybe later" (closes only,
    // no navigation). Full-width, stacked per the design's "Actions" column.
    return [
      {
        id: 'investment-blocked-primary',
        label: copy.primaryKey,
        variant: 'primary',
        size: 'md',
        btnClass: '!w-full',
        onClick: () => {
          this.modal.close();
          this.navigatePrimary();
        },
      },
      {
        id: 'investment-blocked-maybe-later',
        label: 'investment_popup_maybe_later',
        variant: 'tertiary',
        size: 'md',
        btnClass: '!w-full',
        onClick: () => this.modal.close(),
      },
    ];
  }

  private navigatePrimary(): void {
    this.router.navigate(this.resolveInvestmentActionRoute());
  }
}
